import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import _ from 'lodash';
import { parseDate, CalendarDate } from '@internationalized/date';
import { STATUS_VALUES } from './constants';
import {
  isSharedViewPageLoad,
  shouldSkipPreferencePersistence
} from './sharedViewMode';
import { parseViewStateFromUrl } from './viewStateDeepLink';

/**
 * Gets the effective status based on showOnRoadmap and showOnTimeline flags
 * When both flags are true and secondaryDateStatus exists, returns secondaryDateStatus
 * Otherwise returns the regular status
 * @param status - The regular status value
 * @param showOnRoadmap - Flag indicating if shown on roadmap
 * @param showOnTimeline - Flag indicating if shown on timeline
 * @param secondaryDateStatus - The secondary date status (used when both flags are true)
 * @returns The effective status to use
 */
export const getEffectiveStatus = (
  status?: string,
  showOnRoadmap?: boolean,
  showOnTimeline?: boolean,
  secondaryDateStatus?: string
): string => {
  return showOnRoadmap && showOnTimeline && secondaryDateStatus
    ? secondaryDateStatus
    : status || '';
};

/**
 * Normalizes status values to handle both "COMPLETE" and "COMPLETED" formats
 * @param statusValue - The status value to normalize (can be undefined)
 * @returns Normalized status value
 */
export const normalizeStatus = (statusValue?: string): string => {
  if (!statusValue) return statusValue || '';
  // Normalize "COMPLETE" to "COMPLETED" to match STATUS_VALUES.COMPLETE
  if (statusValue.toUpperCase() === 'COMPLETE') {
    return STATUS_VALUES.COMPLETE;
  }
  return statusValue;
};

/**
 * Converts an object of parameters to a URL query string
 */
export const formUrlParameters = (params: Record<string, any>): string => {
  return Object.keys(params)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&');
};

type DateRange = {
  start: CalendarDate;
  end: CalendarDate;
};

export const formatDate = (date: Date) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(date.getDate()).padStart(2, '0')}`;
};

export const getQuarterDates = (
  baseYear: number,
  quarter: number = 0
): DateRange => {
  let startDate: Date, endDate: Date;

  switch (quarter) {
    case 1: // Q1: Dec - Feb
      startDate = new Date(baseYear - 1, 11, 1); // Dec 1
      endDate = new Date(baseYear, 1, new Date(baseYear, 2, 0).getDate()); // Feb 28/29 depending on leap year
      break;
    case 2: // Q2: Mar - May
      startDate = new Date(baseYear, 2, 1); // Mar 1
      endDate = new Date(baseYear, 4, 31); // May 31
      break;
    case 3: // Q3: Jun - Aug
      startDate = new Date(baseYear, 5, 1); // Jun 1
      endDate = new Date(baseYear, 7, 31); // Aug 31
      break;
    case 4: // Q4: Sep - Nov
      startDate = new Date(baseYear, 8, 1); // Sep 1
      endDate = new Date(baseYear, 10, 30); // Nov 30
      break;
    default:
      startDate = new Date(baseYear - 1, 11, 1); // Dec 1
      endDate = new Date(baseYear, 10, 30); // Nov 30
  }

  return {
    start: parseDate(formatDate(startDate)),
    end: parseDate(formatDate(endDate))
  };
};

export const getQuarter = (date: Date): number => {
  const month = date.getMonth();
  // Adjust for fiscal year quarters starting in December
  if (month === 11) {
    // December
    return 1;
  } else if (month >= 0 && month <= 2) {
    // Jan-Mar
    return 1;
  } else if (month >= 3 && month <= 5) {
    // Mar-May
    return 2;
  } else if (month >= 6 && month <= 8) {
    // Jun-Aug
    return 3;
  } else {
    // Sep-Nov
    return 4;
  }
};

export const getMonth = (date: Date): number => {
  return date.getMonth();
};

export const flatten = (into, node) => {
  if (node == null) return into;
  if (Array.isArray(node)) return node.reduce(flatten, into);
  into.push(node);
  return flatten(into, node.children);
};

export const getCookie = name =>
  document.cookie.split('; ').reduce((r, v) => {
    const parts = v.split('=');
    return parts[0] === name ? decodeURIComponent(parts[1]) : r;
  }, '');

/* eslint-disable no-param-reassign */
export const updateScale = (zoomEl, direction) => {
  const zoomoutLimit = 0.2;
  const zoominLimit = 3;
  if (zoomEl) {
    let newScale = 1;
    newScale = direction === 'up' ? newScale + 0.2 : newScale - 0.2;
    const { transform } = zoomEl.style;
    let matrix: any = [];
    let targetScale = 1;
    if (transform === '') {
      zoomEl.style.transform = `matrix(${newScale}, 0, 0, ${newScale}, 0, 0)`;
    } else {
      matrix = transform.split(',');
      if (transform.indexOf('3d') === -1) {
        targetScale = Math.abs(window.parseFloat(matrix[3]) * newScale);
        if (targetScale > zoomoutLimit && targetScale < zoominLimit) {
          matrix[0] = `matrix(${targetScale}`;
          matrix[3] = targetScale;
          zoomEl.style.transform = matrix.join(',');
        }
      } else {
        targetScale = Math.abs(window.parseFloat(matrix[5]) * newScale);
        if (targetScale > zoomoutLimit && targetScale < zoominLimit) {
          matrix[0] = `matrix3d(${targetScale}`;
          matrix[5] = targetScale;
          zoomEl.style.transform = matrix.join(',');
        }
      }
    }
  }
};

export const useQueryParams = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

export const getQueryString = params => {
  if (!Object.keys(params).length) return undefined;
  const queryParams = _.pickBy(params, value => value);
  let query = Object.keys(queryParams)
    .map(key => {
      let value = queryParams[key];
      let valString = value;
      if (Array.isArray(value)) {
        if (typeof value[0] === 'string')
          value = value.map(state => `"${state}"`).join(',');
        valString = `[${value}]`;
      }
      return `${key}=${valString}`;
    })
    .join('&');
  query = `?${query}`;
  return query;
};

export const calculateColSpan = (
  noOfquarters: number,
  isExpanded: boolean = false,
  isCompact: boolean = false,
  index: number = 0 // New parameter to help distribute remainder columns
) => {
  let totalColumns = 14; // Total dynamic columns
  if (isCompact) {
    // totalColumns = 12; // Total dynamic columns
    const baseColSpan = Math.floor(totalColumns / noOfquarters);
    const remainder = totalColumns % noOfquarters;

    // Distribute remainder to first `remainder` items
    return index < remainder ? baseColSpan + 1 : baseColSpan;
  }

  if (noOfquarters === 1) {
    return totalColumns;
  }
  if (isExpanded) {
    switch (noOfquarters) {
      case 2:
        return 9;
      case 3:
        return 8;
      case 4:
        return 5;
      default:
        return totalColumns;
    }
  } else {
    switch (noOfquarters) {
      case 2:
        return 5;
      case 3:
        return 3;
      case 4:
        return 3;
      default:
        return totalColumns;
    }
  }
};

export const getDateRange = (range: String) => {
  let start;
  let end;
  switch (range) {
    case 'Today':
      start = moment();
      end = moment();
      break;
    case 'Yesterday':
      start = moment().subtract(1, 'day');
      end = moment().subtract(1, 'day');
      break;
    case 'Last 7 Days':
      start = moment().subtract(7, 'days');
      end = moment();
      break;
    case 'Last 30 Days':
      start = moment().subtract(30, 'days');
      end = moment();
      break;
    case 'Last 90 Days':
      start = moment().subtract(90, 'days');
      end = moment();
      break;
    case 'Last 180 Days':
      start = moment().subtract(180, 'days');
      end = moment();
      break;
    case 'Last 365 Days':
      start = moment().subtract(365, 'days');
      end = moment();
      break;
    case 'This Week':
      start = moment().startOf('week');
      end = moment().endOf('week');
      break;
    case 'Last Week':
      start = moment().subtract(1, 'week').startOf('week');
      end = moment().subtract(1, 'week').endOf('week');
      break;
    case 'This Month':
      start = moment().startOf('month');
      end = moment().endOf('month');
      break;
    case 'Last Month':
      start = moment().subtract(1, 'month').startOf('month');
      end = moment().subtract(1, 'month').endOf('month');
      break;
    case 'Last 3 Months':
      start = moment().subtract(3, 'months').startOf('month');
      end = moment().subtract(1, 'months').endOf('month');
      break;
    case 'Last 6 Months':
      start = moment().subtract(6, 'months').startOf('month');
      end = moment().subtract(1, 'months').endOf('month');
      break;
    case 'This Year':
      start = moment().startOf('year');
      end = moment().endOf('year');
      break;
    case 'Last Year':
      start = moment().subtract(1, 'year').startOf('year');
      end = moment().subtract(1, 'year').endOf('year');
      break;
    default:
      break;
  }
  return start && end && [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')];
};

// User preferences storage key
const USER_PREFERENCES_KEY = 'roadmap_user_preferences';

export interface UserPreferences {
  productGroups: string[];
  productGroupLabels: string[];
  audiences: string[];
  audienceLabels: string[];
  categories: string[];
  events: string[];
  eventsLabel: string[];
  features: string[];
  products: string[];
  lanesTiers: string[];
  status?: string[];
  categoryProductCategories?: string[];
  categoryProductProducts?: string[];
  genAiFilters?: string[];
  revenueImpactFilters?: string[]; // Revenue Impact filters (Yes/No)
  /** Moment type ids from rsdForms.momentTypes */
  momentTypeFilters?: string[];
  mustWinDropdownSelection?: string | null; // Which item was selected from Must Win dropdown (for blue highlight)
  hideEmptySwimLanes?: boolean; // Hide empty swimlanes when filtering from Must Win dropdown
  surfaces?: string[]; // Surface filters (Desktop, Web, Mobile, etc.)
  isCompact: boolean;
  showParkingLot: boolean;
  mustWinViewActive?: boolean; // Must Wins view toggle state
  hideCompletedItems?: boolean; // CSME-113550: hide COMPLETED/CLOSED items when true (default false = shown)
  isUnNested: boolean;
  isSidebarOpen?: boolean; // Sidebar open/closed state
  collapseAll?: boolean; // Optional: card size preference (big/small)
  expandedQuarters: string[];
  expandedMonths?: string[];
  showBetaView?: boolean; // Milestone grid beta view toggle
  dateRange: {
    start: string;
    end: string;
  };
  timelineViewMode?: 'quarterly' | 'monthly';
  timelineIconsVisible?: boolean;
  communicationModePreference?: 'EMAIL' | 'SLACK'; // Preferred communication mode for comments
}

type PreferenceWriteOptions = {
  /** Bypass shared-view persistence guard (saved-view apply only). */
  force?: boolean;
};

export const saveUserPreferences = (
  preferences: Partial<UserPreferences>,
  options?: PreferenceWriteOptions
) => {
  if (!options?.force && shouldSkipPreferencePersistence()) {
    return;
  }

  try {
    const existingPrefs = getUserPreferences();
    const updatedPrefs = { ...existingPrefs, ...preferences };
    localStorage.setItem(USER_PREFERENCES_KEY, JSON.stringify(updatedPrefs));
  } catch (error) {
    console.error('Error saving user preferences:', error);
  }
};

/** Replace the full preferences object in localStorage (used when applying a saved view). */
export const replaceUserPreferences = (
  preferences: UserPreferences,
  options?: PreferenceWriteOptions
) => {
  if (!options?.force && shouldSkipPreferencePersistence()) {
    return;
  }

  try {
    localStorage.setItem(USER_PREFERENCES_KEY, JSON.stringify(preferences));
  } catch (error) {
    console.error('Error replacing user preferences:', error);
  }
};

export const removeUserPreferenceField = (
  field: keyof UserPreferences,
  options?: PreferenceWriteOptions
): void => {
  if (!options?.force && shouldSkipPreferencePersistence()) {
    return;
  }

  try {
    const existingPrefs = getUserPreferences();
    if (!(field in existingPrefs)) {
      return;
    }
    const updatedPrefs = { ...existingPrefs };
    delete updatedPrefs[field];
    localStorage.setItem(USER_PREFERENCES_KEY, JSON.stringify(updatedPrefs));
  } catch (error) {
    console.error('Error removing user preference field:', error);
  }
};

export function exitCompactModeAndRestoreTimeline(
  timelineViewMode: 'quarterly' | 'monthly' = 'quarterly'
) {
  const preferences = getUserPreferences();
  saveUserPreferences({ isCompact: false });

  if (timelineViewMode === 'monthly') {
    if (
      preferences.expandedMonths &&
      Array.isArray(preferences.expandedMonths) &&
      preferences.expandedMonths.length > 0
    ) {
      window.dispatchEvent(
        new CustomEvent('restoreMonths', {
          detail: { expandedMonths: preferences.expandedMonths }
        })
      );
    }
    return;
  }

  if (
    preferences.expandedQuarters &&
    Array.isArray(preferences.expandedQuarters) &&
    preferences.expandedQuarters.length > 0
  ) {
    window.dispatchEvent(
      new CustomEvent('restoreQuarters', {
        detail: { expandedQuarters: preferences.expandedQuarters }
      })
    );
  }
}

export const getUserPreferences = (): UserPreferences => {
  try {
    const prefsString = localStorage.getItem(USER_PREFERENCES_KEY);
    if (!prefsString) {
      return {
        productGroups: [],
        productGroupLabels: [],
        audiences: [],
        audienceLabels: [],
        categories: [],
        events: [],
        eventsLabel: [],
        features: [],
        products: [],
        lanesTiers: [],
        status: [],
        categoryProductCategories: [],
        categoryProductProducts: [],
        genAiFilters: [],
        momentTypeFilters: [],
        surfaces: [],
        isCompact: false,
        showParkingLot: true,
        isUnNested: false,
        expandedQuarters: [],
        dateRange: {
          start: moment().format('YYYY-MM-DD'),
          end: moment().add(6, 'months').format('YYYY-MM-DD')
        }
      };
    }
    return JSON.parse(prefsString);
  } catch (error) {
    console.error('Error reading user preferences:', error);
    return {
      productGroups: [],
      productGroupLabels: [],
      audiences: [],
      audienceLabels: [],
      categories: [],
      events: [],
      eventsLabel: [],
      features: [],
      products: [],
      lanesTiers: [],
      status: [],
      categoryProductCategories: [],
      categoryProductProducts: [],
      genAiFilters: [],
      momentTypeFilters: [],
      surfaces: [],
      isCompact: false,
      showParkingLot: true,
      isUnNested: false,
      expandedQuarters: [],
      communicationModePreference: 'EMAIL',
      dateRange: {
        start: moment().format('YYYY-MM-DD'),
        end: moment().add(6, 'months').format('YYYY-MM-DD')
      }
    };
  }
};

// Add a helper to get initial date range with preference checking
export const getInitialDateRange = () => {
  const defaultRange = {
    start: moment().format('YYYY-MM-DD'),
    end: moment().add(6, 'months').format('YYYY-MM-DD')
  };

  if (isSharedViewPageLoad()) {
    try {
      const urlParams = parseViewStateFromUrl();
      if (urlParams.startDate && urlParams.endDate) {
        return {
          start: urlParams.startDate,
          end: urlParams.endDate
        };
      }
    } catch (error) {
      console.error('Error loading shared view date range from URL:', error);
    }
    return defaultRange;
  }

  try {
    const preferences = getUserPreferences();
    if (
      preferences.dateRange &&
      preferences.dateRange.start &&
      preferences.dateRange.end
    ) {
      return preferences.dateRange;
    }
  } catch (error) {
    console.error('Error loading date range preferences:', error);
  }

  // Fall back to defaults if no preferences found or on error
  return defaultRange;
};

export const openDocument = (link: string) => {
  window.open(link, '_blank');
};

// Utility function to check labels in a case-agnostic manner
export const isBetaLabel = (label: string): boolean => {
  return label?.toLowerCase() === 'beta';
};

export const isPrivateBetaLabel = (label: string): boolean => {
  return (
    label?.toLowerCase() === 'private-beta' ||
    label?.toLowerCase() === 'private beta'
  );
};

export const isBetaOrPrivateBetaLabel = (label: string): boolean => {
  return isBetaLabel(label) || isPrivateBetaLabel(label);
};

// Function to clear and reset user preferences (useful for removing old showKeyDates: true)
export const resetUserPreferences = () => {
  try {
    localStorage.removeItem(USER_PREFERENCES_KEY);
  } catch (error) {
    console.error('Error resetting user preferences:', error);
  }
};

// Function to clean up old showKeyDates property from localStorage
export const cleanupOldShowKeyDatesPreference = () => {
  try {
    const existingPrefsString = localStorage.getItem(
      'roadmap_user_preferences'
    );
    if (existingPrefsString) {
      const existingPrefs = JSON.parse(existingPrefsString);
      if ('showKeyDates' in existingPrefs) {
        delete existingPrefs.showKeyDates;
        localStorage.setItem(
          'roadmap_user_preferences',
          JSON.stringify(existingPrefs)
        );
      }
    }
  } catch (error) {
    console.error('Error cleaning up old showKeyDates preference:', error);
  }
};

export const getReleasePhaseIcon = (releasePhase: string | undefined) => {
  if (!releasePhase) return null;

  const phase = releasePhase.toLowerCase().trim();
  switch (phase) {
    case 'private beta':
      return 'private-beta';
    case 'public beta':
      return 'beta';
    default:
      return null;
  }
};
/**
 * Converts a quarter string like "Q3 2025" to the end date of that quarter
 * @param quarterString - Quarter string in format "Q1 2025", "Q2 2025", etc.
 * @returns Date object representing the last day of that quarter, or null if invalid
 */
export const getQuarterEndDate = (quarterString: string): Date | null => {
  if (!quarterString) return null;

  const match = quarterString.match(/Q(\d)\s+(\d{4})/);
  if (!match) return null;

  const quarter = parseInt(match[1]);
  const year = parseInt(match[2]);

  if (quarter < 1 || quarter > 4) return null;

  const quarterDates = getQuarterDates(year, quarter);
  return new Date(quarterDates.end.toString());
};

/**
 * Converts a month string like "Jan", "Feb", "March", etc. to the end date of that month in the current year
 * @param monthString - Month string in format "Jan", "Feb", "March", etc.
 * @param year - Optional year, defaults to current year
 * @returns Date object representing the last day of that month, or null if invalid
 */
export const getMonthEndDate = (
  monthString: string,
  year?: number
): Date | null => {
  if (!monthString) return null;

  const currentYear = year || new Date().getFullYear();

  // Map of month names to month numbers (0-based for JavaScript Date)
  const monthMap: { [key: string]: number } = {
    jan: 0,
    january: 0,
    feb: 1,
    february: 1,
    mar: 2,
    march: 2,
    apr: 3,
    april: 3,
    may: 4,
    jun: 5,
    june: 5,
    jul: 6,
    july: 6,
    aug: 7,
    august: 7,
    sep: 8,
    september: 8,
    oct: 9,
    october: 9,
    nov: 10,
    november: 10,
    dec: 11,
    december: 11
  };

  const monthKey = monthString.toLowerCase().trim();
  const monthIndex = monthMap[monthKey];

  if (monthIndex === undefined) return null;

  // Create date for last day of the month
  const lastDayOfMonth = new Date(currentYear, monthIndex + 1, 0);
  return lastDayOfMonth;
};
